<template>
  <!-- Loading -->
  <LoadingDiv v-if="isInitialLoading" />

  <!-- Auth -->
  <template v-else>
    <div class="section center amber-text text-darken-4" v-if="sourceWarning">
      <small>{{ sourceWarning }}</small>
    </div>

    <div class="card z-depth-0" v-for="claim in claims"
      :key="`${claim.history_source || 'easyhmo'}:${claim.id}`">
    <div class="card-panel z-depth-0">
      <div class="row">
        <div class="col s3 hide-on-small-only">
          <h6>
            {{ claim.encounter.name }}
            <small><span class="blue-text">{{
              showAgeSex(claim.encounter.dob, claim.encounter.sex)
            }}</span></small>
            <br />
            <small>{{ claim.encounter.enrollee_number }}</small>
            <br />
            <small>({{ claim.encounter.client_name }})</small>
          </h6>
        </div>
        <div class="col s5 m2">
          <p>{{ claim.encounter.provider.name }}</p>
        </div>
        <div class="col s7 m2">
          <p>
            <b>{{ claim.encounter.diagnosis }}</b>
          </p>
        </div>
        <div class="col s12 m2">
          <p>
            {{ claim.item }}
            <span class="black-text">(x<b>{{ claim.sent_qty }}</b>)</span>
            <br />
            <span v-if="claim.sent_price" :class="{
              'amber-text text-darken-4': claim.authorization === 'z' && claim.status === 'a',
              'black-text': !(claim.authorization === 'z' && claim.status === 'a'),
            }">₦{{
                Number(claim.sent_price)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }}
            </span>
          </p>
        </div>
        <div class="col s7 m2">
          <p>
            <span v-if="claim.authorization === 'z'">
              <span class="chip" v-if="claim.status === 'a'">Pending</span>
              <span class="chip red lighten-2" v-else>Declined</span>
            </span>

            <span v-else>{{ claim.authorization_code }}<br />
              <span class="chip green lighten-2" v-if="claim.approved_price"><b>₦{{
                Number(claim.approved_price)
                  .toFixed(2)
                  .toString()
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
              }}</b>
              </span>
              <span v-else>₦0.00</span>
            </span>
          </p>
        </div>
        <div class="col s5 m1">
          <p class="right" :class="{
            'black-text': claim.authorization === 'z',
            'green-text': claim.authorization !== 'z',
          }">
            {{
              new Date(claim.created_at).toLocaleString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              })
            }}
          </p>
        </div>
      </div>
    </div>
    </div>

    <div class="section center" v-if="nextLoad">
      <button
        class="btn-flat waves-effect history-load-more"
        :class="{ disabled: isLoading }"
        @click.prevent="handlePage(nextLoad)"
      >
        <span v-if="!isLoading">Load more history</span>
        <span v-else>Loading history...</span>
      </button>
      <div class="preloader-wrapper small active history-inline-loader" v-if="isLoading">
        <div class="spinner-layer spinner-blue-only">
          <div class="circle-clipper left">
            <div class="circle"></div>
          </div>
          <div class="gap-patch">
            <div class="circle"></div>
          </div>
          <div class="circle-clipper right">
            <div class="circle"></div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <!-- No Auth Found -->
  <template v-if="!hideUnFound">
    <div class="section center" v-if="!claims.length && !isLoading && hasLoaded">
      <div class="card z-depth-0">
      <h6 class="chip yellow">No Previous Authorization <em>found</em>...</h6>
      </div>
    </div>
  </template>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import getLocalItems from "@/composables/getItems";
import { siloMode } from "@/laravel/config";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "HistoryAuthComponent",
  components: { LoadingDiv },
  props: ["enrolleeNumber", "principalId", "schemeId", "hideUnFound"],
  setup(props) {
    const response = ref(null);
    const error = ref(false);
    const isLoading = ref(false);
    const hasLoaded = ref(false);
    const nextLoad = ref(null);
    const localHistoryStarted = ref(siloMode);
    const sourceWarning = ref(null);

    const appendRows = (rows, source) => {
      const sourcedRows = rows.map(claim => ({ ...claim, history_source: source }));
      response.value = response.value
        ? [...response.value, ...sourcedRows]
        : sourcedRows;
    };

    const localHistoryQuery = (page) => {
      const query = new URLSearchParams({
        e: props.enrolleeNumber || "",
        page: String(page),
      });
      const principalId = Number(props.principalId);
      const schemeId = Number(props.schemeId);

      if (principalId > 0) {
        query.set("principal_id", String(principalId));
      }

      if (schemeId > 0) {
        query.set(siloMode ? "s" : "scheme_id", String(schemeId));
      }

      if (!siloMode) {
        query.set("submission_scope", "submitted");
      }

      return query;
    };

    const loadLocalHistory = async (page) => {
      isLoading.value = true;
      const query = localHistoryQuery(page);
      const { res, err } = await getLocalItems(`claim/auth/enrollee?${query.toString()}`);

      if (res.value) {
        appendRows(res.value.data.data || [], "easyhmo");
        nextLoad.value = res.value.data.last_page > res.value.data.current_page
          ? res.value.data.current_page + 1
          : null;
      } else {
        error.value = err.value;
        nextLoad.value = page;
      }

      isLoading.value = false;
      hasLoaded.value = true;
    };

    const loadOneProviderHistory = async () => {
      isLoading.value = true;
      const query = new URLSearchParams({ e: props.enrolleeNumber || "" });
      const schemeId = Number(props.schemeId);

      if (schemeId > 0) {
        query.set("scheme_id", String(schemeId));
      }

      const { res, err } = await getLocalItems(
        `integration/oneprovider/auth/history?${query.toString()}`
      );
      const rows = res.value?.data?.data || [];

      if (rows.length) {
        appendRows(rows, "oneprovider");
        nextLoad.value = 1;
        isLoading.value = false;
        hasLoaded.value = true;
        return;
      }

      if (!res.value) {
        error.value = err.value;
        sourceWarning.value = "Recent oneProvider history is unavailable. Showing submitted history.";
      }

      isLoading.value = false;
      localHistoryStarted.value = true;
      await loadLocalHistory(1);
    };

    onMounted(() => {
      if (siloMode) {
        loadLocalHistory(1);
      } else {
        loadOneProviderHistory();
      }
    });

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // switch page
    const handlePage = (page) => {
      if (isLoading.value) {
        return;
      }

      if (!siloMode && !localHistoryStarted.value) {
        localHistoryStarted.value = true;
      }

      loadLocalHistory(page);
    };

    const isInitialLoading = computed(() => {
      return isLoading.value && !hasLoaded.value;
    });

    // output results
    const claims = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    return {
      showAgeSex,
      error,
      isLoading,
      isInitialLoading,
      hasLoaded,
      nextLoad,
      sourceWarning,
      handlePage,
      claims,
    };
  },
};
</script>

<style scoped>
.history-load-more {
  text-transform: none;
}

.history-inline-loader {
  display: block;
  margin: 10px auto 0;
}
</style>
